import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { Divider, ListItem } from '@mui/material';
import {
  AlignIcon,
  ChecklistIcon,
  DeleteIcon,
  ExternalLinkIcon,
  InboundFileIcon,
  RotateLeftIcon,
  RotateRightIcon,
  ShowIcon,
  SyncIcon,
  TreeDiagramIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { DOCUMENTS_ROUTING_PARAMS, DocumentsPaths } from 'app/configs/routesConfig/documents';
import { documentsApi } from 'app/services/api';
import {
  ContextMenu,
  ContextMenuItem,
  ContextSubMenu,
} from 'app/shared/components/common/ContextMenu';
import SearchInput from 'app/shared/components/common/SearchInput';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { INACTIVE_CATEGORY_ID } from 'constants/documentCategories';
import { DocumentRotationDirection } from 'constants/documents';
import { PopoverReference } from 'constants/popover';
import {
  checkIsDocumentUndeletable,
  checkIsMalwareBlocked,
  checkIsRecipientCopy,
} from 'utils/documents';
import { getFileDateTimeName } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';
import { openURLInNewTab } from 'utils/navigation';

import { deleteDocumentAction } from 'store/documents/actions';
import { openConfirmationDialogAction, showErrorMessageAction } from 'store/global/actions';
import {
  selectDocumentTypeNameByDocumentId,
  selectDocumentTypesWithCategories,
  selectIsDocumentTypeWorksheetEnabled,
} from 'store/system/selectors';

import { DocumentContextMenuTestIds } from './DocumentContextMenu.testIds';

const renderInput = params => (
  <SearchInput
    autoFocus
    isFilled
    isWithIcon
    ariaLabel={params.inputProps['aria-label']}
    inputProps={params.inputProps}
    inputRef={params.InputProps.ref}
    onChange={params.inputProps.onChange}
  />
);

const dangerItemSx = { color: 'error.main' };

const DocumentContextMenu = ({
  anchorPosition,
  onClose,
  onMoveDocumentToType,
  onRotateDocumentWithSave,
  onSplitPages,
  onOpenWorksheet,
  document,
  isDocumentLoaded,
  isSplitDisabled,
}) => {
  const { id: documentId, documentTypeId, isDeleted, isMalware } = document || {};

  const isMalwareBlocked = checkIsMalwareBlocked(isMalware);
  const isRecipientCopy = checkIsRecipientCopy(document);
  const isUndeletable = checkIsDocumentUndeletable(document);

  const dispatch = useDispatch();
  const history = useHistory();

  const documentTypeName = useSelector(selectDocumentTypeNameByDocumentId(documentId));
  const documentTypesOptions = useSelector(selectDocumentTypesWithCategories);
  const isWorksheetEnabled = useSelector(selectIsDocumentTypeWorksheetEnabled(documentTypeId));

  const filteredDocumentTypesOptions = useMemo(
    () =>
      documentTypesOptions.filter(
        option =>
          option.documentCategoryId !== INACTIVE_CATEGORY_ID &&
          option.documentTypeId !== documentTypeId,
      ),
    [documentTypesOptions, documentTypeId],
  );

  const groupByDocumentTypes = useCallback(option => option.documentCategoryName, []);

  const onPreviewDocument = useCallback(() => {
    history.push(
      DocumentsPaths.documentPreview.generatePath({
        [DOCUMENTS_ROUTING_PARAMS.docId]: documentId,
      }),
    );
    onClose();
  }, [documentId, history, onClose]);

  const onOpenDocumentInNewTab = useCallback(() => {
    const href = history.createHref({
      pathname: DocumentsPaths.documentPreview.generatePath({
        [DOCUMENTS_ROUTING_PARAMS.docId]: documentId,
      }),
    });

    openURLInNewTab(href);
    onClose();
  }, [documentId, history, onClose]);

  const onDownloadDocument = useCallback(async () => {
    try {
      const fileName = getFileDateTimeName({ fileName: documentTypeName, extension: 'pdf' });
      const { data: pdfBlob } = await documentsApi.getContent({ documentId });

      await downloadOrShareBlob(pdfBlob, fileName);
    } catch (err) {
      dispatch(showErrorMessageAction({ message: 'Could not download document', err }));
      logger.error(err);
    }

    onClose();
  }, [dispatch, documentId, documentTypeName, onClose]);

  const onDeleteDocument = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Confirm document delete',
        description: 'Are you sure you want to delete this document?',
        onAccept: () => {
          dispatch(deleteDocumentAction({ documentId }));
          onClose();
        },
      }),
    );
    onClose();
  }, [dispatch, documentId, onClose]);

  const moveSubmenu = (
    <ContextSubMenu
      icon={InboundFileIcon}
      id="move"
      isDisabled={isDeleted || isRecipientCopy}
      label="Move to type…"
    >
      <ListItem dense className="px-0">
        <AutocompleteCDS
          freeSolo
          isIgnorePins
          open
          ariaLabel="Move to type"
          containerClassName="min-w-320"
          dataTestId={DocumentContextMenuTestIds.MOVE_TYPE_SELECT}
          groupBy={groupByDocumentTypes}
          name="documentType"
          optionName="documentTypeName"
          options={filteredDocumentTypesOptions}
          renderInput={renderInput}
          size="small"
          onChange={onMoveDocumentToType}
        />
      </ListItem>
    </ContextSubMenu>
  );

  const rotateSubmenu = (
    <ContextSubMenu
      icon={SyncIcon}
      id="rotate"
      isDisabled={isDeleted || !isDocumentLoaded || isRecipientCopy}
      label="Rotate & save"
    >
      <ContextMenuItem
        data-direction={DocumentRotationDirection.LEFT}
        icon={RotateLeftIcon}
        label="Rotate Left"
        onClick={onRotateDocumentWithSave}
      />
      <ContextMenuItem
        data-direction={DocumentRotationDirection.RIGHT}
        icon={RotateRightIcon}
        label="Rotate Right"
        onClick={onRotateDocumentWithSave}
      />
    </ContextSubMenu>
  );

  return (
    <ContextMenu
      open
      anchorPosition={anchorPosition}
      anchorReference={PopoverReference.ANCHOR_POSITION}
      onClose={onClose}
    >
      <ContextMenuItem icon={ShowIcon} label="Preview" onClick={onPreviewDocument} />
      {!isMalwareBlocked && (
        <ContextMenuItem
          icon={ExternalLinkIcon}
          label="Open in new tab"
          onClick={onOpenDocumentInNewTab}
        />
      )}

      {isWorksheetEnabled && !!onOpenWorksheet && (
        <ContextMenuItem
          data-testid={DocumentContextMenuTestIds.OPEN_WORKSHEET}
          icon={ChecklistIcon}
          label="Open worksheet"
          onClick={onOpenWorksheet}
        />
      )}

      <Divider />

      {moveSubmenu}
      {rotateSubmenu}

      <ContextMenuItem
        icon={TreeDiagramIcon}
        isDisabled={isSplitDisabled || isRecipientCopy}
        label="Split pages"
        onClick={onSplitPages}
      />
      {!isMalwareBlocked && (
        <ContextMenuItem icon={AlignIcon} label="Download" onClick={onDownloadDocument} />
      )}

      <Divider />

      <ContextMenuItem
        icon={DeleteIcon}
        isDisabled={isUndeletable}
        label="Delete"
        sx={dangerItemSx}
        onClick={onDeleteDocument}
      />
    </ContextMenu>
  );
};

DocumentContextMenu.propTypes = {
  anchorPosition: PropTypes.object.isRequired,
  document: PropTypes.object.isRequired,
  isDocumentLoaded: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMoveDocumentToType: PropTypes.func.isRequired,
  onRotateDocumentWithSave: PropTypes.func.isRequired,
  isSplitDisabled: PropTypes.bool,
  onSplitPages: PropTypes.func,
  onOpenWorksheet: PropTypes.func,
};

export default memo(DocumentContextMenu);
